/**
 * Typed HTTP helpers wrapping the shared Axios client.
 * Centralizes response unwrapping and common error-shape checks.
 */

import type { AxiosRequestConfig } from 'axios';
import httpClient from '@/services/http-client';

/**
 * Returns true when an error looks like an Axios response with the given HTTP status.
 *
 * @param error - Caught error from a request
 * @param status - HTTP status code to match (e.g. 404)
 */
export function isAxiosErrorWithStatus(error: unknown, status: number): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    (error as { response?: { status?: number } }).response?.status === status
  );
}

/**
 * Performs a GET request and returns the response body.
 *
 * @param url - Request URL (relative to gateway base)
 * @param config - Optional Axios request config
 * @returns Parsed response body
 */
export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await httpClient.get<T>(url, config);
  return response.data;
}

/**
 * Performs a GET request and returns null when the server responds with 404.
 * Re-throws all other errors.
 *
 * @param url - Request URL (relative to gateway base)
 * @param config - Optional Axios request config
 * @returns Parsed response body, or null on 404
 */
export async function apiGetOrNullOn404<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T | null> {
  try {
    return await apiGet<T>(url, config);
  } catch (error) {
    if (isAxiosErrorWithStatus(error, 404)) {
      return null;
    }
    throw error;
  }
}

/**
 * Performs a POST request and returns the response body.
 *
 * @param url - Request URL (relative to gateway base)
 * @param body - Request payload
 * @param config - Optional Axios request config
 * @returns Parsed response body
 */
export async function apiPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await httpClient.post<T>(url, body, config);
  return response.data;
}

/**
 * Performs a PATCH request and returns the response body.
 *
 * @param url - Request URL (relative to gateway base)
 * @param body - Request payload
 * @param config - Optional Axios request config
 * @returns Parsed response body
 */
export async function apiPatch<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await httpClient.patch<T>(url, body, config);
  return response.data;
}

/**
 * Performs a PUT request and returns the response body.
 *
 * @param url - Request URL (relative to gateway base)
 * @param body - Request payload
 * @param config - Optional Axios request config
 * @returns Parsed response body
 */
export async function apiPut<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig
): Promise<T> {
  const response = await httpClient.put<T>(url, body, config);
  return response.data;
}

/**
 * Performs a DELETE request and returns the response body.
 *
 * @param url - Request URL (relative to gateway base)
 * @param config - Optional Axios request config
 * @returns Parsed response body
 */
export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await httpClient.delete<T>(url, config);
  return response.data;
}
